"use client";

import React, { useState, useMemo, useRef } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import UseAnimations from "react-useanimations";
import download from "react-useanimations/lib/download";
import { ExternalLink, Package } from "lucide-react";
import { AddonsIconsCluster } from "@/components/ui/AddonsIconsCluster";
import { CONTENT_WIDTH_CLASS } from "@/lib/constants";
import { MI } from "@/lib/microinteractions";
import { useFirebaseData } from "@/hooks/useFirebaseData";
import { AddonInfo } from "@/lib/types";
import { isVideo } from "@/lib/utils";
import { useForceMuted } from "@/hooks/useForceMuted";
import { useBlenderDragInstall } from "@/hooks/useBlenderDragInstall";

// Fallback when Firebase has no addons yet (e.g. before seed)
const FALLBACK_ADDONS: AddonInfo[] = [
  {
    id: "fallback-1",
    name: "MutiRangeRenderer",
    isVisible: true,
    version: "V1.1.1",
    description:
      "Render specific frame ranges or stills with ease. Fix faulty renders by re-rendering only what you need. Queue as many ranges as you want — no limits.",
    downloadHref: "https://files.koen.work/media/2025/09/MultiRangeRenderer-1.zip",
    imageUrl: "/media/addons/multi-range-renderer.avif",
    order: 0,
  },
  {
    id: "fallback-2",
    name: "CopyRender",
    isVisible: true,
    version: "V1.2.0",
    description:
      "Copy render settings to your clipboard and paste them into any Blender file with two clicks. Save time, avoid mistakes, keep setups consistent across projects.",
    downloadHref: "https://files.koen.work/media/2025/09/CopyRender.zip",
    imageUrl: "/media/addons/copy-render.avif",
    order: 1,
  },
  {
    id: "fallback-3",
    name: "Listen, Children!",
    isVisible: true,
    version: "V1.5.0",
    description:
      "Adds drivers to all child objects automatically. Hide the parent — children follow. Simple to use, saves time on complex hierarchies.",
    downloadHref: "https://files.koen.work/media/2025/09/Listen_Children_v1_5.zip",
    imageUrl: "/media/addons/listen-children.avif",
    order: 2,
  },
];

export const AddonsPage: React.FC = () => {
  const { allAddons } = useFirebaseData();
  const addons = useMemo(() => {
    const source = allAddons.length > 0 ? allAddons : FALLBACK_ADDONS;
    return [...source]
      .filter((a) => a.isVisible !== false)
      .sort((a, b) => a.order - b.order);
  }, [allAddons]);
  return (
    <div className="relative min-h-screen w-full text-white font-sans">
      {/* Custom Hero with Icons Cluster */}
      <div className="relative w-full overflow-hidden">
        <div className="w-[90%] sm:w-[80%] mx-auto pt-4 sm:pt-[70px] pb-8 lg:pb-12 min-h-[380px] xl:min-h-0">
          {/* Mobile: heading/subheader full width, cluster in background. xl: side by side */}
          <div className="relative flex flex-col xl:flex-row portrait:flex-row landscape:flex-row items-start xl:items-center justify-between gap-6 xl:gap-6">
            {/* Title and Subtitle — full width on mobile, below cluster; xl: side by side */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "circOut" }}
              className="relative z-10 w-full xl:w-[55%] xl:landscape:w-[55%] text-left portrait:mt-[280px] portrait:sm:mt-[320px] xl:mt-0"
            >
              <div className="flex flex-col items-start xl:grid xl:grid-cols-[1.15fr_1.2fr] xl:items-center xl:justify-items-start xl:gap-x-12">
                <h1
                  className="text-4xl sm:text-3xl md:text-4xl lg:text-4xl font-extrabold xl:max-w-none [text-wrap:pretty] mb-8 sm:mb-10 xl:mb-0 xl:leading-[1.333] leading-tight"
                  style={{ fontWeight: 800 }}
                >
                  My Blender <span className="whitespace-nowrap">add&#8209;ons</span> —
                  <br />
                  small tools, big
                  <br />
                  time-savers
                </h1>
                <div className="min-w-0 md:max-w-none xl:max-w-[80ch] w-full">
                  <div className="text-xl sm:text-base md:text-lg lg:text-lg text-white/80 font-light [text-wrap:pretty] xl:leading-[1.333]">
                    Most add-ons try to do everything — these do one thing well. Lightweight, focused, no fluff. I built these to solve specific problems I kept running into. <span className="text-[#aaff00] font-medium">Free to download and use.</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Icons Cluster — mobile portrait: above headline, centered; mobile landscape: bottom-right; xl: side by side */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "circOut" }}
              className="pointer-events-none absolute right-0 bottom-0 left-auto top-auto translate-x-0 portrait:top-0 portrait:left-[15%] portrait:-translate-x-1/2 portrait:right-auto portrait:bottom-auto w-[85%] max-w-[350px] h-[280px] sm:h-[320px] xl:pointer-events-auto xl:static xl:left-auto xl:right-auto xl:bottom-auto xl:translate-x-0 xl:w-[48%] xl:max-w-none xl:h-[400px] overflow-visible z-0"
            >
              <AddonsIconsCluster className="w-full h-full portrait:max-w-[350px]" />
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2, ease: "circOut" }}
        className={`${CONTENT_WIDTH_CLASS} px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24 pt-16 pb-24 sm:pb-32`}
      >
        <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {addons.map((addon, index) => (
              <AddonCard key={addon.id} addon={addon} index={index} />
            ))}
          </div>
      </motion.div>
    </div>
  );
};

// Shared lime pill styling for every add-on download / drag button.
const ADDON_BTN_CLASS =
  "inline-flex items-center gap-2 px-4 py-2.5 bg-[#aaff00] hover:bg-[#b8ff33] text-black text-sm font-medium rounded-full transition-colors duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#aaff00]/50";

const AddonCard: React.FC<{ addon: AddonInfo; index: number }> = ({ addon, index }) => {
  const [mediaError, setMediaError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaUrl = addon.imageUrl?.trim();
  const showVideo = mediaUrl && isVideo(mediaUrl) && !mediaError;
  const showImage = mediaUrl && !isVideo(mediaUrl) && !mediaError;
  const isExtension = addon.downloadButtonIcon === 'extension';
  const { isDragging, onDragStart, onDragEnd } = useBlenderDragInstall();

  useForceMuted(videoRef);

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: 0.1 * index,
        ease: "circOut",
      }}
      className="bg-white/[0.03] backdrop-blur-sm rounded-3xl border-[0.5px] border-white/[0.08] overflow-hidden flex flex-col hover:border-white/[0.16] hover:bg-white/[0.05] transition-colors duration-300"
    >
      {showVideo && (
        <div className="relative w-full aspect-video overflow-hidden bg-black/20">
          <video
            ref={videoRef}
            src={mediaUrl}
            className="w-full h-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            onError={() => setMediaError(true)}
          />
        </div>
      )}
      {showImage && (
        <div className="relative w-full aspect-video overflow-hidden bg-black/20">
          <Image
            src={mediaUrl}
            alt={addon.name}
            fill
            className="object-cover"
            onError={() => setMediaError(true)}
            unoptimized
          />
        </div>
      )}
      <div className="p-6 flex flex-col flex-grow">
      <h2 className="text-xl font-bold text-white/95 tracking-tight mb-2">
        {addon.name}
      </h2>
      <p className="text-white/80 font-light text-sm leading-relaxed flex-grow mb-6">
        {addon.description}
      </p>
      <div className="flex items-center justify-between gap-4 mt-auto">
        <span className="text-white/60 text-sm font-medium">
          {addon.version}
        </span>
        {isExtension ? (
          <>
            {/* Mouse devices only: drag the .zip into Blender to install + register the repo */}
            <div
              className="relative hidden mouse:inline-flex"
              // The button stays fully opaque and is covered by the dashed placeholder;
              // a tilted clone is used as the native drag ghost (see useBlenderDragInstall).
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              <motion.a
                href={addon.downloadHref}
                // Native anchor drag puts the href (the absolute .zip URL) into the drag
                // payload; dropping it into Blender 4.2+ installs & enables the extension.
                draggable
                title="Drag into Blender to install"
                className={`${ADDON_BTN_CLASS} cursor-move`}
                aria-label={`Drag ${addon.name} into Blender to install`}
                whileTap={{ scale: MI.tapScale }}
                transition={{ duration: MI.tapDuration }}
              >
                <Package size={16} />
                {addon.downloadButtonLabel || "Drag to Blender"}
              </motion.a>
              {isDragging && (
                // Opaque base (page background colour) fully covers the still-visible
                // button; the inner layer reproduces the empty dashed-slot look.
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-[#06090c] pointer-events-none"
                >
                  <span className="absolute inset-0 rounded-full border-2 border-dashed border-[#aaff00]/50 bg-[#aaff00]/10" />
                </span>
              )}
            </div>
            {/* Touch (phones + tablets): no Blender / no drag — plain download of the same .zip */}
            <motion.a
              href={addon.downloadHref}
              download
              className={`${ADDON_BTN_CLASS} mouse:hidden`}
              aria-label={`Download ${addon.name}`}
              whileTap={{ scale: MI.tapScale }}
              transition={{ duration: MI.tapDuration }}
            >
              <UseAnimations animation={download} size={16} strokeColor="currentColor" />
              Download
            </motion.a>
          </>
        ) : (
          <motion.a
            href={addon.downloadHref}
            target={addon.downloadButtonIcon === 'external-link' ? '_blank' : undefined}
            rel={addon.downloadButtonIcon === 'external-link' ? 'noopener noreferrer' : undefined}
            className={ADDON_BTN_CLASS}
            aria-label={`Download ${addon.name}`}
            whileTap={{ scale: MI.tapScale }}
            transition={{ duration: MI.tapDuration }}
          >
            {addon.downloadButtonIcon === 'external-link' ? (
              <ExternalLink size={16} />
            ) : (
              <UseAnimations animation={download} size={16} strokeColor="currentColor" />
            )}
            {addon.downloadButtonLabel || "Download"}
          </motion.a>
        )}
      </div>
      </div>
    </motion.article>
  );
};
